import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Button,
  Avatar,
  IconButton,
  Collapse,
  Skeleton,
  Snackbar,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Users,
  Save,
  Search,
  Trophy,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Wallet,
  Lock,
  Gift,
  Percent,
  UserPlus,
  Plus,
  Trash2,
  TrendingUp,
} from 'lucide-react';

const API_BASE_URL = 'https://api.playbro.com/affiliateAdmin';

/* ─── Interfaces ─── */
interface DashboardStats {
  totalTeams: number;
  totalReferrals: number;
  totalLocked: number;
  totalDistributed: number;
  totalRewardRecords: number;
  totalClaimed: number;
  totalUnclaimed: number;
  claimedCount: number;
  unclaimedCount: number;
}
interface AffiliateSettings {
  affiliatebonus: number;
  comissionpercent: number;
  registerbonus: number;
  affiliatecurrency: string;
  affiliate?: boolean;
}
interface TeamMember {
  id: number; name: string; email: string; avatar: string;
  level: number; balance: number | null; games_played: number;
}
interface Team {
  ownername: string; referalCode: string; createdAt: string; updatedAt: string;
  owner_id: number; owner_name: string; owner_email: string; owner_avatar: string;
  owner_level: number; members: TeamMember[];
}
interface RewardRecord {
  ownername: string; membername: string; referalCode: string; amount: number;
  coin: string; type: string; locked: boolean; referalmount: number;
  createdAt: string; member_email: string; member_level: number;
}
interface TopAffiliate {
  ownername: string; referral_count: string; email: string; level: number; total_earned: string;
}
interface ClaimRecord {
  id: number; uid: number; ownername: string; membername: string; referalCode: string;
  amount: number; cointype: string; wager_amount: number; claimed: boolean;
  createdAt: string; claimed_at: string | null; user_name: string | null; user_email: string | null;
}

interface Tier {
  id?: number;
  wager_threshold: number;
  reward_amount: number;
}

type TabId = 'dashboard' | 'settings' | 'tiers' | 'teams' | 'rewards' | 'claims';
const CURRENCY_OPTIONS = ['inr', 'usdt', 'usdc', 'busd', 'eur', 'bdt', 'npr', 'pkr', 'aed'];

/* ─── Shared dark styles (match Challenges/spinWheel) ─── */
const cardSx = { bgcolor: '#0E1831', border: '1px solid #1E2D55', borderRadius: 2 };
const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#0B1427',
    '& fieldset': { borderColor: '#1E2D55' },
    '&:hover fieldset': { borderColor: '#2B6EF5' },
    '&.Mui-focused fieldset': { borderColor: '#2B6EF5' },
  },
  '& input': { color: '#F9F9F9' },
  '& .MuiSelect-select': { color: '#F9F9F9' },
  '& .MuiInputLabel-root': { color: '#8384A5' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#2B6EF5' },
};
const thSx = {
  bgcolor: '#0B1427', color: '#8384A5', fontWeight: 700, fontSize: '0.72rem',
  textTransform: 'uppercase' as const, letterSpacing: '0.06em',
  borderBottom: '1px solid #1E2D55', whiteSpace: 'nowrap' as const,
};
const tdSx = { color: '#C8CAE5', borderBottom: '1px solid #1E2D55', fontSize: '0.82rem', py: 1.4 };

const fmt = (n: number | string) => parseFloat(String(n ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const initial = (s?: string) => (s || '?').charAt(0).toUpperCase();
const stringToColor = (s: string): string => {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  let color = '#';
  for (let i = 0; i < 3; i++) color += ('00' + ((hash >> (i * 8)) & 0xff).toString(16)).slice(-2);
  return color;
};

/* ─── Stat card ─── */
const STAT_COLORS: Record<string, string> = {
  blue: '#5581F7', green: '#0ECC68', yellow: '#FFC23F', orange: '#FF8A3D',
  teal: '#2FD8C5', red: '#E01B4F', violet: '#7B5EF5',
};
const StatCard: React.FC<{ label: string; value: string | number; color: string; icon?: React.ReactNode }> = ({ label, value, color, icon }) => {
  const c = STAT_COLORS[color] || STAT_COLORS.blue;
  return (
    <Paper sx={{ ...cardSx, p: 2.25, position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', bgcolor: c }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography sx={{ color: '#8384A5', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
          <Typography sx={{ color: '#F9F9F9', fontSize: '1.5rem', fontWeight: 700, mt: 0.5 }}>{value}</Typography>
        </Box>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${c}22`, display: 'flex' }}>{icon}</Box>
      </Stack>
    </Paper>
  );
};

/* ─── Main ─── */
const Referal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });
  const notify = (msg: string, sev: 'success' | 'error' = 'success') => setSnack({ open: true, msg, sev });

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [topAffiliates, setTopAffiliates] = useState<TopAffiliate[]>([]);
  const [settings, setSettings] = useState<AffiliateSettings>({ affiliatebonus: 0, comissionpercent: 0, registerbonus: 0, affiliatecurrency: 'inr' });

  const [tiers, setTiers] = useState<Tier[]>([]);
  const [tiersCurrency, setTiersCurrency] = useState('inr');
  const [savingTiers, setSavingTiers] = useState(false);

  const [teams, setTeams] = useState<Team[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);

  const [rewards, setRewards] = useState<RewardRecord[]>([]);
  const [rewardsPage, setRewardsPage] = useState(1);
  const [rewardsTotalPages, setRewardsTotalPages] = useState(1);

  const [claims, setClaims] = useState<ClaimRecord[]>([]);
  const [claimsPage, setClaimsPage] = useState(1);
  const [claimsTotalPages, setClaimsTotalPages] = useState(1);
  const [claimsStatus, setClaimsStatus] = useState<'all' | 'claimed' | 'unclaimed'>('all');
  const [totalClaimedAmount, setTotalClaimedAmount] = useState(0);

  /* ── fetchers ── */
  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const [statsRes, topRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dashboard-stats`),
        axios.get(`${API_BASE_URL}/top-affiliates?limit=10`),
      ]);
      if (statsRes.data.success) setStats(statsRes.data.data);
      if (topRes.data.success) setTopAffiliates(topRes.data.data);
    } catch (e) { notify('Failed to load dashboard', 'error'); }
    finally { setLoading(false); }
  };
  const fetchSettings = async () => {
    try { setLoading(true); const r = await axios.get(`${API_BASE_URL}/settings`); if (r.data.success) setSettings(r.data.data); }
    catch (e) { notify('Failed to load settings', 'error'); } finally { setLoading(false); }
  };
  const fetchTeams = async () => {
    try { setLoading(true); const r = await axios.get(`${API_BASE_URL}/teams`); if (r.data.success) setTeams(r.data.data); }
    catch (e) { notify('Failed to load teams', 'error'); } finally { setLoading(false); }
  };
  const fetchRewards = async (page = 1) => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_BASE_URL}/rewards-list?page=${page}&limit=30`);
      if (r.data.success) { setRewards(r.data.data); setRewardsTotalPages(r.data.pagination.totalPages || 1); setRewardsPage(page); }
    } catch (e) { notify('Failed to load rewards', 'error'); } finally { setLoading(false); }
  };
  const fetchClaims = async (page = 1, status = claimsStatus) => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_BASE_URL}/claim-history?page=${page}&limit=30&status=${status}`);
      if (r.data.success) {
        setClaims(r.data.data); setClaimsTotalPages(r.data.pagination.totalPages || 1);
        setClaimsPage(page); setTotalClaimedAmount(r.data.totalClaimedAmount || 0);
      }
    } catch (e) { notify('Failed to load claim history', 'error'); } finally { setLoading(false); }
  };

  const fetchTiers = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_BASE_URL}/tiers`);
      if (r.data.success) { setTiers(r.data.data); setTiersCurrency(r.data.currency || 'inr'); }
    } catch (e) { notify('Failed to load tiers', 'error'); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') fetchDashboard();
    if (activeTab === 'settings') fetchSettings();
    if (activeTab === 'tiers') fetchTiers();
    if (activeTab === 'teams') fetchTeams();
    if (activeTab === 'rewards') fetchRewards(1);
    if (activeTab === 'claims') fetchClaims(1, claimsStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'claims') fetchClaims(1, claimsStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimsStatus]);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const r = await axios.put(`${API_BASE_URL}/settings`, settings);
      if (r.data.success) { setSettings(r.data.data); notify('Settings saved successfully!'); }
    } catch (e) { notify('Failed to save settings', 'error'); } finally { setSaving(false); }
  };

  const setTier = (idx: number, field: keyof Tier, value: number) =>
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  const addTier = () => setTiers((prev) => [...prev, { wager_threshold: 0, reward_amount: 0 }]);
  const removeTier = (idx: number) => setTiers((prev) => prev.filter((_, i) => i !== idx));
  const handleSaveTiers = async () => {
    if (!tiers.length) return notify('Add at least one tier', 'error');
    if (tiers.some((t) => !(t.wager_threshold >= 0) || !(t.reward_amount > 0)))
      return notify('Each tier needs a valid wager threshold and reward > 0', 'error');
    try {
      setSavingTiers(true);
      const r = await axios.put(`${API_BASE_URL}/tiers`, { tiers });
      if (r.data.success) { setTiers(r.data.data); notify('Reward tiers saved!'); }
    } catch (e) { notify('Failed to save tiers', 'error'); } finally { setSavingTiers(false); }
  };

  const filteredTeams = teams.filter((t) =>
    t.members.length > 0 &&
    ((t.owner_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.referalCode || '').toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const tabs: { id: TabId; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'settings', label: 'Settings' },
    { id: 'tiers', label: 'Reward Tiers' },
    { id: 'teams', label: 'Teams' },
    { id: 'rewards', label: 'Rewards Log' },
    { id: 'claims', label: 'Claim History' },
  ];

  const Pager: React.FC<{ page: number; totalPages: number; onPage: (p: number) => void }> = ({ page, totalPages, onPage }) =>
    totalPages > 1 ? (
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: '1px solid #1E2D55', gap: 1.5 }}>
        <Button size="small" disabled={page <= 1} onClick={() => onPage(page - 1)} startIcon={<ChevronLeft size={14} />}
          sx={{ border: '1px solid #1E2D55', color: '#8384A5', textTransform: 'none', fontSize: '0.75rem', '&:hover': { borderColor: '#2B6EF5', color: '#2B6EF5' }, '&:disabled': { opacity: 0.4 } }}>Prev</Button>
        <Typography variant="caption" sx={{ color: '#8384A5' }}>Page {page} of {totalPages}</Typography>
        <Button size="small" disabled={page >= totalPages} onClick={() => onPage(page + 1)} endIcon={<ChevronRight size={14} />}
          sx={{ border: '1px solid #1E2D55', color: '#8384A5', textTransform: 'none', fontSize: '0.75rem', '&:hover': { borderColor: '#2B6EF5', color: '#2B6EF5' }, '&:disabled': { opacity: 0.4 } }}>Next</Button>
      </Box>
    ) : null;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{ p: 1, bgcolor: 'rgba(123,94,245,0.15)', borderRadius: 2, display: 'flex' }}>
          <Users size={22} color="#7B5EF5" />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ color: '#F9F9F9', fontWeight: 700 }}>Affiliate Management</Typography>
          <Typography variant="caption" sx={{ color: '#8384A5' }}>Invite-friends rewards, commission, teams & claim history</Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3, minHeight: 0,
          '& .MuiTabs-indicator': { backgroundColor: '#2B6EF5', height: 3, borderRadius: 3 },
          '& .MuiTab-root': { color: '#8384A5', textTransform: 'none', fontWeight: 600, minHeight: 0, py: 1.25, '&.Mui-selected': { color: '#F9F9F9' } },
        }}
      >
        {tabs.map((t) => <Tab key={t.id} value={t.id} label={t.label} />)}
      </Tabs>

      {loading && (
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={90} sx={{ bgcolor: '#162140', borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={260} sx={{ bgcolor: '#162140', borderRadius: 2 }} />
        </Stack>
      )}

      {/* ═══ Dashboard ═══ */}
      {activeTab === 'dashboard' && !loading && stats && (
        <Stack spacing={3}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: 2 }}>
            <StatCard label="Total Teams" value={stats.totalTeams} color="violet" icon={<Users size={18} color={STAT_COLORS.violet} />} />
            <StatCard label="Total Referrals" value={stats.totalReferrals} color="blue" icon={<UserPlus size={18} color={STAT_COLORS.blue} />} />
            <StatCard label="Total Distributed" value={`$${fmt(stats.totalDistributed)}`} color="green" icon={<Wallet size={18} color={STAT_COLORS.green} />} />
            <StatCard label="Total Claimed" value={`$${fmt(stats.totalClaimed)}`} color="teal" icon={<Gift size={18} color={STAT_COLORS.teal} />} />
            <StatCard label="Total Locked" value={`$${fmt(stats.totalLocked)}`} color="yellow" icon={<Lock size={18} color={STAT_COLORS.yellow} />} />
            <StatCard label="Total Unclaimed" value={`$${fmt(stats.totalUnclaimed)}`} color="orange" icon={<Wallet size={18} color={STAT_COLORS.orange} />} />
            <StatCard label="Claimed Count" value={stats.claimedCount} color="teal" icon={<Gift size={18} color={STAT_COLORS.teal} />} />
            <StatCard label="Unclaimed Count" value={stats.unclaimedCount} color="red" icon={<Lock size={18} color={STAT_COLORS.red} />} />
          </Box>

          <Paper sx={{ ...cardSx, overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2, bgcolor: '#0B1427', borderBottom: '1px solid #1E2D55', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Trophy size={16} color="#FFC23F" />
              <Typography sx={{ color: '#F9F9F9', fontWeight: 700 }}>Top Affiliates</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>#</TableCell>
                    <TableCell sx={thSx}>Username</TableCell>
                    <TableCell sx={thSx}>Email</TableCell>
                    <TableCell sx={thSx}>Level</TableCell>
                    <TableCell sx={thSx}>Referrals</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: 'right' }}>Total Earned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topAffiliates.length ? topAffiliates.map((a, i) => (
                    <TableRow key={a.ownername} sx={{ '&:hover': { bgcolor: 'rgba(43,110,245,0.04)' } }}>
                      <TableCell sx={{ ...tdSx, color: '#8384A5' }}>{i + 1}</TableCell>
                      <TableCell sx={tdSx}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: stringToColor(a.ownername) }}>{initial(a.ownername)}</Avatar>
                          <Typography sx={{ color: '#F9F9F9', fontSize: '0.82rem', fontWeight: 600 }}>{a.ownername}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, color: '#8384A5' }}>{a.email || '—'}</TableCell>
                      <TableCell sx={tdSx}><Chip label={`Lv ${a.level || 1}`} size="small" sx={{ bgcolor: 'rgba(14,204,104,0.12)', color: '#0ECC68', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                      <TableCell sx={{ ...tdSx, color: '#5581F7', fontWeight: 700 }}>{a.referral_count}</TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right', color: '#0ECC68', fontWeight: 600, fontFamily: 'monospace' }}>${fmt(a.total_earned)}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, borderBottom: 'none', color: '#8384A5' }}>No affiliates found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      {/* ═══ Settings ═══ */}
      {activeTab === 'settings' && !loading && (
        <Paper sx={{ ...cardSx, maxWidth: 720, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#0B1427', borderBottom: '1px solid #1E2D55' }}>
            <Typography sx={{ color: '#F9F9F9', fontWeight: 700 }}>Affiliate Reward Settings</Typography>
            <Typography variant="caption" sx={{ color: '#8384A5' }}>Configure rewards, commission and payout currency. Changes apply to rewards unlocked from now on.</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <TextField label="Joining Reward (Referral Bonus)" type="number" size="small" fullWidth sx={inputSx}
                value={settings.affiliatebonus} onChange={(e) => setSettings({ ...settings, affiliatebonus: parseFloat(e.target.value) || 0 })}
                helperText="Credited to the referrer when a new user signs up (locked until wager milestones)."
                FormHelperTextProps={{ sx: { color: '#8384A5', ml: 0 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Gift size={15} color="#8384A5" /></InputAdornment> }} />
              <TextField label="Commission Percentage" type="number" size="small" fullWidth sx={inputSx}
                value={settings.comissionpercent} onChange={(e) => setSettings({ ...settings, comissionpercent: parseFloat(e.target.value) || 0 })}
                helperText="Percentage of a referred user's wager paid as commission."
                FormHelperTextProps={{ sx: { color: '#8384A5', ml: 0 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Percent size={15} color="#8384A5" /></InputAdornment> }} />
              <TextField label="Registration Bonus" type="number" size="small" fullWidth sx={inputSx}
                value={settings.registerbonus} onChange={(e) => setSettings({ ...settings, registerbonus: parseFloat(e.target.value) || 0 })}
                helperText="Welcome bonus credited to a new user upon registration."
                FormHelperTextProps={{ sx: { color: '#8384A5', ml: 0 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><UserPlus size={15} color="#8384A5" /></InputAdornment> }} />
              <TextField select label="Payout Currency" size="small" sx={{ ...inputSx, maxWidth: 220 }}
                value={settings.affiliatecurrency} onChange={(e) => setSettings({ ...settings, affiliatecurrency: e.target.value })}
                helperText="Wallet credited when an affiliate claims a reward."
                FormHelperTextProps={{ sx: { color: '#8384A5', ml: 0 } }}>
                {CURRENCY_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>)}
              </TextField>
            </Stack>
            <Button onClick={handleSaveSettings} disabled={saving} variant="contained" startIcon={<Save size={15} />}
              sx={{ mt: 3, bgcolor: '#2B6EF5', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#4A85F7' } }}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* ═══ Reward Tiers ═══ */}
      {activeTab === 'tiers' && !loading && (
        <Paper sx={{ ...cardSx, maxWidth: 760, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#0B1427', borderBottom: '1px solid #1E2D55', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUp size={16} color="#FFC23F" />
              <Typography sx={{ color: '#F9F9F9', fontWeight: 700 }}>Reward Unlock Tiers</Typography>
            </Stack>
            <Chip label={`Paid in ${tiersCurrency.toUpperCase()}`} size="small" sx={{ bgcolor: 'rgba(43,110,245,0.12)', color: '#5581F7', fontWeight: 600 }} />
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography variant="caption" sx={{ color: '#8384A5', display: 'block', mb: 2 }}>
              A referred user unlocks the highest tier whose wager threshold their cumulative wager reaches.
              Rewards are credited in the configured payout currency ({tiersCurrency.toUpperCase()}).
            </Typography>

            {/* Column headers */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 44px', gap: 1.5, px: 0.5, mb: 1 }}>
              {['Wager Threshold', `Reward (${tiersCurrency.toUpperCase()})`, ''].map((h) => (
                <Typography key={h} variant="caption" sx={{ color: '#8384A5', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase' }}>{h}</Typography>
              ))}
            </Box>

            <Stack spacing={1}>
              {tiers.map((t, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 44px', gap: 1.5, alignItems: 'center' }}>
                  <TextField size="small" type="number" value={t.wager_threshold}
                    onChange={(e) => setTier(i, 'wager_threshold', parseFloat(e.target.value) || 0)} sx={inputSx} />
                  <TextField size="small" type="number" value={t.reward_amount}
                    onChange={(e) => setTier(i, 'reward_amount', parseFloat(e.target.value) || 0)} sx={inputSx} />
                  <IconButton size="small" onClick={() => removeTier(i)} sx={{ color: '#8384A5', '&:hover': { color: '#E01B4F' } }}>
                    <Trash2 size={16} />
                  </IconButton>
                </Box>
              ))}
              {!tiers.length && (
                <Typography sx={{ color: '#8384A5', textAlign: 'center', py: 3 }}>No tiers. Add one below.</Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
              <Button startIcon={<Plus size={15} />} onClick={addTier}
                sx={{ bgcolor: '#162140', color: '#F9F9F9', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#1C2B4A' } }}>
                Add Tier
              </Button>
              <Button startIcon={<Save size={15} />} onClick={handleSaveTiers} disabled={savingTiers} variant="contained"
                sx={{ bgcolor: '#2B6EF5', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#4A85F7' }, '&:disabled': { bgcolor: '#162140', color: '#8384A5' } }}>
                {savingTiers ? 'Saving...' : 'Save Tiers'}
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* ═══ Teams ═══ */}
      {activeTab === 'teams' && !loading && (
        <Paper sx={{ ...cardSx, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#0B1427', borderBottom: '1px solid #1E2D55', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#F9F9F9', fontWeight: 700 }}>Teams</Typography>
            <TextField size="small" placeholder="Search owner or code…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ ...inputSx, minWidth: 240 }} InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} color="#8384A5" /></InputAdornment> }} />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Owner</TableCell>
                  <TableCell sx={thSx}>Email</TableCell>
                  <TableCell sx={thSx}>Referral Code</TableCell>
                  <TableCell sx={thSx}>Members</TableCell>
                  <TableCell sx={thSx}>Created</TableCell>
                  <TableCell sx={{ ...thSx, width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTeams.length ? filteredTeams.map((team) => (
                  <React.Fragment key={team.ownername}>
                    <TableRow hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(43,110,245,0.04)' } }}
                      onClick={() => setExpandedTeam(expandedTeam === team.ownername ? null : team.ownername)}>
                      <TableCell sx={tdSx}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 30, height: 30, fontSize: '0.8rem', bgcolor: stringToColor(team.owner_name || '') }}>{initial(team.owner_name)}</Avatar>
                          <Typography sx={{ color: '#F9F9F9', fontSize: '0.82rem', fontWeight: 600 }}>{team.owner_name || 'Unknown'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, color: '#8384A5' }}>{team.owner_email || '—'}</TableCell>
                      <TableCell sx={tdSx}><Chip label={team.referalCode || 'N/A'} size="small" sx={{ bgcolor: 'rgba(43,110,245,0.12)', color: '#5581F7', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                      <TableCell sx={{ ...tdSx, color: '#F9F9F9', fontWeight: 600 }}>{team.members.length}</TableCell>
                      <TableCell sx={{ ...tdSx, color: '#8384A5' }}>{fmtDate(team.createdAt)}</TableCell>
                      <TableCell sx={tdSx}>
                        <ChevronDown size={16} color="#8384A5" style={{ transform: expandedTeam === team.ownername ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedTeam === team.ownername ? '1px solid #1E2D55' : 'none' }}>
                        <Collapse in={expandedTeam === team.ownername} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: '#0B1427' }}>
                            <Typography variant="caption" sx={{ color: '#8384A5', fontWeight: 700, textTransform: 'uppercase' }}>Members ({team.members.length})</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
                              {team.members.map((m) => (
                                <Paper key={m.id} sx={{ bgcolor: '#0E1831', border: '1px solid #1E2D55', borderRadius: 1.5, p: 1.25, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: stringToColor(m.name || '') }}>{initial(m.name)}</Avatar>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ color: '#F9F9F9', fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || 'Unknown'}</Typography>
                                    <Typography sx={{ color: '#8384A5', fontSize: '0.68rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email || '—'}</Typography>
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )) : (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, borderBottom: 'none', color: '#8384A5' }}>No teams found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ═══ Rewards Log ═══ */}
      {activeTab === 'rewards' && !loading && (
        <Paper sx={{ ...cardSx, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#0B1427', borderBottom: '1px solid #1E2D55' }}>
            <Typography sx={{ color: '#F9F9F9', fontWeight: 700 }}>Rewards Log</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Referrer</TableCell>
                  <TableCell sx={thSx}>Member</TableCell>
                  <TableCell sx={thSx}>Code</TableCell>
                  <TableCell sx={thSx}>Type</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>Original</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>Remaining</TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={thSx}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rewards.length ? rewards.map((r, i) => (
                  <TableRow key={i} sx={{ '&:hover': { bgcolor: 'rgba(43,110,245,0.04)' } }}>
                    <TableCell sx={{ ...tdSx, color: '#F9F9F9', fontWeight: 600 }}>{r.ownername}</TableCell>
                    <TableCell sx={{ ...tdSx, color: '#8384A5' }}>{r.membername}</TableCell>
                    <TableCell sx={tdSx}><Chip label={r.referalCode} size="small" sx={{ bgcolor: 'rgba(43,110,245,0.12)', color: '#5581F7', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                    <TableCell sx={{ ...tdSx, textTransform: 'capitalize' }}>{r.type}</TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'right', color: '#8384A5', fontFamily: 'monospace' }}>${fmt(r.referalmount || 0)}</TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'right', color: '#F9F9F9', fontFamily: 'monospace' }}>${fmt(r.amount || 0)}</TableCell>
                    <TableCell sx={tdSx}>
                      <Chip label={r.locked ? 'Locked' : 'Unlocked'} size="small"
                        sx={{ bgcolor: r.locked ? 'rgba(255,194,63,0.12)' : 'rgba(14,204,104,0.12)', color: r.locked ? '#FFC23F' : '#0ECC68', fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, color: '#8384A5' }}>{fmtDate(r.createdAt)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 6, borderBottom: 'none', color: '#8384A5' }}>No reward records found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Pager page={rewardsPage} totalPages={rewardsTotalPages} onPage={fetchRewards} />
        </Paper>
      )}

      {/* ═══ Claim History ═══ */}
      {activeTab === 'claims' && !loading && (
        <Paper sx={{ ...cardSx, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#0B1427', borderBottom: '1px solid #1E2D55', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography sx={{ color: '#F9F9F9', fontWeight: 700 }}>Claim History</Typography>
              <Chip label={`$${fmt(totalClaimedAmount)} claimed`} size="small" sx={{ bgcolor: 'rgba(14,204,104,0.12)', color: '#0ECC68', fontWeight: 600 }} />
            </Stack>
            <Stack direction="row" spacing={1}>
              {(['all', 'claimed', 'unclaimed'] as const).map((s) => (
                <Button key={s} size="small" onClick={() => setClaimsStatus(s)}
                  sx={{
                    textTransform: 'capitalize', fontSize: '0.75rem', px: 1.5,
                    border: '1px solid #1E2D55',
                    bgcolor: claimsStatus === s ? '#2B6EF5' : 'transparent',
                    color: claimsStatus === s ? '#fff' : '#8384A5',
                    '&:hover': { bgcolor: claimsStatus === s ? '#4A85F7' : '#162140' },
                  }}>{s}</Button>
              ))}
            </Stack>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>User</TableCell>
                  <TableCell sx={thSx}>Referrer</TableCell>
                  <TableCell sx={thSx}>Code</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>Amount</TableCell>
                  <TableCell sx={thSx}>Currency</TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={thSx}>Claimed At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claims.length ? claims.map((c) => (
                  <TableRow key={c.id} sx={{ '&:hover': { bgcolor: 'rgba(43,110,245,0.04)' } }}>
                    <TableCell sx={{ ...tdSx, color: '#F9F9F9', fontWeight: 600 }}>{c.user_name || c.membername || `#${c.uid}`}</TableCell>
                    <TableCell sx={{ ...tdSx, color: '#8384A5' }}>{c.ownername}</TableCell>
                    <TableCell sx={tdSx}><Chip label={c.referalCode} size="small" sx={{ bgcolor: 'rgba(43,110,245,0.12)', color: '#5581F7', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'right', color: '#F9F9F9', fontFamily: 'monospace' }}>{fmt(c.amount)}</TableCell>
                    <TableCell sx={{ ...tdSx, textTransform: 'uppercase', color: '#8384A5' }}>{c.cointype}</TableCell>
                    <TableCell sx={tdSx}>
                      <Chip label={c.claimed ? 'Claimed' : 'Unclaimed'} size="small"
                        sx={{ bgcolor: c.claimed ? 'rgba(14,204,104,0.12)' : 'rgba(255,194,63,0.12)', color: c.claimed ? '#0ECC68' : '#FFC23F', fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, color: '#8384A5' }}>{fmtDate(c.claimed_at)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, borderBottom: 'none', color: '#8384A5' }}>No claim records found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Pager page={claimsPage} totalPages={claimsTotalPages} onPage={(p) => fetchClaims(p, claimsStatus)} />
        </Paper>
      )}

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.sev}
          sx={{
            bgcolor: snack.sev === 'success' ? 'rgba(14,204,104,0.15)' : 'rgba(224,27,79,0.15)',
            color: snack.sev === 'success' ? '#0ECC68' : '#E01B4F',
            border: `1px solid ${snack.sev === 'success' ? '#0ECC68' : '#E01B4F'}44`,
            '& .MuiAlert-icon': { color: snack.sev === 'success' ? '#0ECC68' : '#E01B4F' },
          }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Referal;
